<script lang="ts" setup>
import { menuQueries } from '@services/asp-c/queries/Menu';
import { useAdviseursGetHuidigeAdviseur } from '@services/crm-public/orval-api/adviseurs-api/adviseurs-api';
import { SUREbusinessCommonDomainAdviseurStatus as AdviseurStatus } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainAdviseurStatus';
import VMenu, { type VMenuItem } from '@shared/component/VMenu/VMenu.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useQuery } from '@tanstack/vue-query';
import type { MenuItemCommandEvent } from 'primevue/menuitem';
import { computed } from 'vue';
import { useClaims } from '@auth/useClaims';
import { hasPermission } from '@permission/hasPermission';
import SUREbusinessLogo from '@assets/icons/SUREbusinessLogo.vue';
import SUREprivateLogo from '@assets/icons/SUREprivateLogo.vue';
import { useRecenteActiviteitenGetTellingen } from '@services/overig/orval-api/recente-activiteiten/recente-activiteiten';
import { useSpfGetSpfMenuStatus } from '@services/overig/orval-api/spf/spf';
import { type HeaderItem, type LinkItem, type MenuItem } from './types';
import { getSurenetUrl, tryMenuNavigation } from '../../urlHelper';
import AppLayoutContact from './AppLayoutContact.vue';
import AppLayoutMenuAccount from './AppLayoutMenuAccount.vue';
import AppLayoutMenuIntern from './AppLayoutMenuIntern.vue';
import { createMenuItems } from './mainMenuItems';

const props = defineProps<{
  customItems?: Array<MenuItem>;
  customLoadingState?: boolean;
  navigateInFrame?: boolean;
}>();

const open = defineModel<boolean>('open');
const searchUserOpen = defineModel<boolean>('searchUserOpen');

const claims = useClaims();

const { data: tellingen } = useRecenteActiviteitenGetTellingen({
  query: {
    select: (res) => res.result,
    enabled: computed(() => claims.value?.isSuccess && !props.customItems),
  },
});

const { data: instellingenTelling } = useSpfGetSpfMenuStatus({
  query: {
    select: (res) => res.result,
    enabled: computed(
      () =>
        claims.value?.isSuccess && claims.value.heeftInstellingenPermissie && !props.customItems,
    ),
  },
});

const aantalPremievastellingQuery = useQuery({
  ...menuQueries.getAantalPremievastellingen,
  select: (result) => result.data,
  enabled: computed(() => claims.value?.isSuccess && !props.customItems),
});

const huidigeAdviseur = useAdviseursGetHuidigeAdviseur({
  query: { select: (data) => data.result },
});

const heeftMutatieStartenPermissie = hasPermission('Muteren.MutatieStarten');
const heeftRoyerenPermissie = hasPermission('Muteren.Royeren');
const heeftVoorlopigeDekkingPermissie = hasPermission('VoorlopigeDekking.Inzien');
const heeftRisicoscanPermissie = hasPermission('Risicoscan.Inzien');

const menuItemsHoofdMenu = computed(() =>
  createMenuItems({
    marktsegment: claims.value?.marktsegment,
    isMaatschappij: claims.value?.isMaatschappij ?? false,
    isInterneGebruiker: claims.value?.isInternal ?? false,
    isFinancieel: claims.value?.isFinancieel ?? false,
    isInstellingsRechten: claims.value?.heeftInstellingenPermissie ?? false,
    isActiveAdviseur:
      (claims.value?.isAdviseur ?? false) &&
      huidigeAdviseur.data.value?.status === AdviseurStatus.Actief,
    heeftAdviseurbeheerRechten: claims.value?.magAdviseurbeheerInzien ?? false,
    heeftBeheerboxRechten: claims.value?.magBeheerboxInzien ?? false,
    heeftMutatieStartenPermissie: heeftMutatieStartenPermissie.value ?? false,
    heeftRoyerenPermissie: heeftRoyerenPermissie.value ?? false,
    heeftVoorlopigeDekkingPermissie: heeftVoorlopigeDekkingPermissie.value ?? false,
    heeftRisicoscanPermissie: heeftRisicoscanPermissie.value ?? false,
    tellingen: {
      ...tellingen.value,
      aantalPremievastellingen: aantalPremievastellingQuery.data.value,
      spfMeldingen: instellingenTelling.value,
    },
  }),
);

const menuItems = computed(() => props.customItems || menuItemsHoofdMenu.value || []);
const model = computed(() => menuItems.value.reduce(menuItemsReducer, []));

function closeMenu() {
  open.value = false;
}

function menuItemClicked(url: string) {
  return ({ originalEvent }: MenuItemCommandEvent) => {
    if (!tryMenuNavigation(originalEvent, url)) {
      return;
    }

    open.value = false;
  };
}

const parseMenuItem = ({
  badgecontent,
  iconclass,
  showbadge,
  target,
  title,
  url,
}: LinkItem): VMenuItem => ({
  badge: showbadge ? badgecontent?.toString() : '',
  command: !target ? menuItemClicked(url) : undefined,
  icon: iconclass,
  label: title,
  target,
  url: props.navigateInFrame ? url : getSurenetUrl(url),
});

const parseMenuTitle = ({ title }: HeaderItem) => ({
  items: [],
  label: title,
});

function menuItemsReducer(items: VMenuItem[], item: MenuItem) {
  if (item.itemtype === 'header') {
    items.push(parseMenuTitle(item));
  } else {
    const [lastRootItem] = items.slice(-1);
    (lastRootItem?.items ? lastRootItem.items : items).push(parseMenuItem(item));
  }

  return items;
}

const isLoading = computed(
  () =>
    (props.customItems ? props.customLoadingState : claims.value.isInitialLoading) ||
    huidigeAdviseur.isPending.value,
);
</script>

<template>
  <nav
    class="fixed max-3xl:inset-0 max-3xl:z-header 3xl:sticky 3xl:top-[var(--header-height,0)] 3xl:h-[calc(100dvh-var(--header-height,0px))]"
    :class="{
      'max-3xl:pointer-events-none max-3xl:opacity-0 max-3xl:transition-[clip-path,opacity] max-3xl:delay-[.4s,0s] max-3xl:duration-[0s,.4s] max-3xl:clip-path-[circle(0_at_1.5rem_calc(var(--header-height,0px)/2))]':
        !open,
      'max-3xl:transition-[clip-path] max-3xl:duration-400 max-3xl:clip-path-[circle(calc(hypot(100dvw,100dvh)/2))]':
        open,
    }"
  >
    <div
      class="flex h-full scrollbar-thin scrollbar-light flex-col gap-5 overflow-y-auto bg-menu text-white sm:w-[340px] 3xl:w-auto"
    >
      <header class="sticky top-0 z-10 flex shrink-0 flex-col gap-5 bg-inherit pb-2 3xl:hidden">
        <div class="grid h-(--header-height,auto) grid-cols-[1fr_auto_1fr] gap-2 px-2 lg:px-3">
          <div class="flex">
            <button
              class="border-none bg-transparent px-3 text-lg text-inherit outline-none"
              type="button"
              @click="closeMenu"
            >
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" />
            </button>
          </div>

          <a class="flex items-center text-inherit" href="/app/aanvraag/recente-activiteiten">
            <component
              :is="claims?.isParticulier ? SUREprivateLogo : SUREbusinessLogo"
              class="h-6 -translate-y-[5px]"
              theme="light"
            />
          </a>

          <div class="flex justify-end py-4">
            <AppLayoutMenuIntern
              v-if="claims?.isInternal"
              v-model:search-user-open="searchUserOpen"
              always-small
              icon-style="fas"
            />

            <AppLayoutMenuAccount always-small icon-style="fas" />
          </div>
        </div>

        <div class="flex shrink-0 px-4 max-sm:justify-center">
          <AppLayoutContact :particulier="claims?.isParticulier" class="p-2 text-white/70" />
        </div>
      </header>

      <div class="grow pb-1 max-3xl:px-2 3xl:py-8">
        <div class="flex flex-col gap-8" v-if="isLoading">
          <VSkeleton height="lg" :count="6" />
          <div class="flex flex-col gap-3">
            <VSkeleton class="max-w-24" />
            <VSkeleton height="lg" :count="8" />
          </div>
          <div class="flex flex-col gap-3">
            <VSkeleton class="max-w-24" />
            <VSkeleton height="lg" :count="4" />
          </div>
        </div>
        <VMenu
          v-else
          class="w-full"
          :model
          :pt="{ list: { 'aria-label': 'Hoofdmenu' } }"
          theme="primary"
        />
      </div>
    </div>

    <aside
      class="absolute inset-0 -z-10 cursor-pointer bg-black/50 backdrop-blur-xs 3xl:hidden"
      @click="closeMenu"
    ></aside>
  </nav>
</template>
